import { useState, useRef } from 'react';
import { useAuthStore } from '../../stores/authStore';
import AdminLayout from '../../components/admin/AdminLayout';
import api from '../../utils/api';
import toast from 'react-hot-toast';

export default function ProfilePage() {
    const { user, setAuth, updateUser } = useAuthStore();
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);
    
    const [formData, setFormData] = useState({
        username: user?.username || '',
        email: user?.email || '',
        bio: user?.bio || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleAvatarClick = () => {
        fileInputRef.current?.click();
    };

    const handleAvatarChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validation
        if (!file.type.startsWith('image/')) {
            toast.error('Veuillez sélectionner une image');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            toast.error('L\'image ne doit pas dépasser 5 Mo');
            return;
        }

        setUploading(true);
        const formData = new FormData();
        formData.append('avatar', file);

        try {
            const response = await api.post('/users/avatar', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.success) {
                // Mise à jour du user dans le store
                updateUser({ avatar: response.data.avatar });
                toast.success('Photo de profil mise à jour !');
            }
        } catch (error) {
            console.error('Erreur upload avatar:', error);
            toast.error(error.response?.data?.error || 'Erreur lors de l\'upload');
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            // Validation des mots de passe
            if (formData.newPassword && formData.newPassword !== formData.confirmPassword) {
                toast.error('Les mots de passe ne correspondent pas');
                setLoading(false);
                return;
            }

            const updateData = {
                username: formData.username,
                bio: formData.bio
            };

            // Ajouter le mot de passe uniquement si renseigné
            if (formData.newPassword) {
                updateData.currentPassword = formData.currentPassword;
                updateData.newPassword = formData.newPassword;
            }

            const response = await api.put('/users/profile', updateData);

            if (response.data.success) {
                // Mise à jour du user dans le store
                updateUser(response.data.user);
                
                toast.success('Profil mis à jour !');
                
                // Reset password fields
                setFormData({
                    ...formData,
                    username: response.data.user.username,
                    bio: response.data.user.bio || '',
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                });
            }
        } catch (error) {
            console.error('Erreur mise à jour profil:', error);
            toast.error(error.response?.data?.error || 'Erreur lors de la mise à jour');
        } finally {
            setLoading(false);
        }
    };

    return (
        <AdminLayout>
            <div className="p-8">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-white mb-2">Mon profil</h1>
                    <p className="text-gray-400">Gérez vos informations personnelles</p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Card Avatar */}
                    <div className="lg:col-span-1">
                        <div className="bg-surface rounded-xl p-6 border border-gray-700/30">
                            <h3 className="text-lg font-semibold text-white mb-4">Photo de profil</h3>
                            
                            <div className="flex flex-col items-center">
                                <div 
                                    className="relative group cursor-pointer mb-4"
                                    onClick={handleAvatarClick}
                                >
                                    <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-yellow-400/20 group-hover:border-yellow-400/50 transition-all">
                                        {user?.avatar ? (
                                            <img 
                                                src={user.avatar}
                                                alt={user.username}
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-full h-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center">
                                                <i className="fas fa-user text-gray-900 text-5xl"></i>
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Overlay au hover */}
                                    <div className="absolute inset-0 rounded-full bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                        <i className="fas fa-camera text-white text-2xl"></i>
                                    </div>

                                    {uploading && (
                                        <div className="absolute inset-0 rounded-full bg-black/80 flex items-center justify-center">
                                            <i className="fas fa-spinner fa-spin text-white text-2xl"></i>
                                        </div>
                                    )}
                                </div>

                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleAvatarChange}
                                    className="hidden"
                                />

                                <button
                                    onClick={handleAvatarClick}
                                    disabled={uploading}
                                    className="px-4 py-2 bg-yellow-400/10 border border-yellow-400/30 text-yellow-400 rounded-lg hover:bg-yellow-400/20 transition-all disabled:opacity-50"
                                >
                                    <i className="fas fa-upload mr-2"></i>
                                    {uploading ? 'Upload...' : 'Changer la photo'}
                                </button>

                                <p className="text-gray-500 text-xs mt-3 text-center">
                                    JPG, PNG ou GIF. Max 5 Mo.
                                </p>
                            </div>

                            {/* Infos utilisateur */}
                            <div className="mt-6 pt-6 border-t border-gray-700/30 space-y-3">
                                <div>
                                    <p className="text-gray-500 text-xs">Rôle</p>
                                    <p className="text-white font-medium capitalize">
                                        {user?.role === 'contributor' ? 'Contributeur' : 
                                         user?.role === 'admin' ? 'Administrateur' : 
                                         user?.role}
                                    </p>
                                </div>

                                {user?.whmcs_client_id && (
                                    <div>
                                        <p className="text-gray-500 text-xs">Client WHMCS</p>
                                        <p className="text-white font-medium">#{user.whmcs_client_id}</p>
                                    </div>
                                )}

                                {user?.role === 'contributor' && (
                                    <>
                                        <div>
                                            <p className="text-gray-500 text-xs">Récompenses totales</p>
                                            <p className="text-yellow-400 font-bold text-lg">
                                                {parseFloat(user?.total_rewards || 0).toFixed(2)} €
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-gray-500 text-xs">Articles publiés</p>
                                            <p className="text-white font-medium">{user?.articles_approved || 0}</p>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Card Informations */}
                    <div className="lg:col-span-2">
                        <div className="bg-surface rounded-xl p-6 border border-gray-700/30">
                            <h3 className="text-lg font-semibold text-white mb-6">Informations personnelles</h3>

                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            Nom d'utilisateur
                                        </label>
                                        <input
                                            type="text"
                                            name="username"
                                            value={formData.username}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 bg-background border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            Email
                                            {user?.whmcs_client_id && (
                                                <span className="ml-2 text-xs text-gray-500">(Synchronisé WHMCS)</span>
                                            )}
                                        </label>
                                        <input
                                            type="email"
                                            value={formData.email}
                                            className="w-full px-4 py-3 bg-background/50 border border-gray-700 rounded-lg text-gray-400 cursor-not-allowed"
                                            disabled
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Biographie
                                    </label>
                                    <textarea
                                        name="bio"
                                        value={formData.bio}
                                        onChange={handleChange}
                                        rows="4"
                                        className="w-full px-4 py-3 bg-background border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent resize-none"
                                        placeholder="Parlez-nous de vous..."
                                    />
                                </div>

                                {/* Changement de mot de passe (seulement pour comptes locaux) */}
                                {!user?.whmcs_client_id && (
                                    <div className="pt-6 border-t border-gray-700/30">
                                        <h4 className="text-md font-semibold text-white mb-4">Changer le mot de passe</h4>
                                        
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                                    Mot de passe actuel
                                                </label>
                                                <input
                                                    type="password"
                                                    name="currentPassword"
                                                    value={formData.currentPassword}
                                                    onChange={handleChange}
                                                    className="w-full px-4 py-3 bg-background border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                                                />
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                                        Nouveau mot de passe
                                                    </label>
                                                    <input
                                                        type="password"
                                                        name="newPassword"
                                                        value={formData.newPassword}
                                                        onChange={handleChange}
                                                        className="w-full px-4 py-3 bg-background border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                                        Confirmer le mot de passe
                                                    </label>
                                                    <input
                                                        type="password"
                                                        name="confirmPassword"
                                                        value={formData.confirmPassword}
                                                        onChange={handleChange}
                                                        className="w-full px-4 py-3 bg-background border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="flex justify-end pt-4">
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="px-6 py-3 bg-gradient-to-r from-yellow-400 to-yellow-600 hover:from-yellow-500 hover:to-yellow-700 text-gray-900 font-bold rounded-lg transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                                    >
                                        {loading ? (
                                            <>
                                                <i className="fas fa-spinner fa-spin mr-2"></i>
                                                Enregistrement...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-save mr-2"></i>
                                                Enregistrer les modifications
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
}
